import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import { Route, RoutePayload } from '../models/route.model';
import { RouteStop, RouteStopPayload } from '../models/route-stop.model';
import { normalizeApiArray } from '../utils/api-response.util';

@Injectable({
  providedIn: 'root'
})
export class RouteService {
  private routeUrl = `${environment.apiUrl}/Route`;
  private routeStopUrl = `${environment.apiUrl}/RouteStop`;

  constructor(private http: HttpClient) { }

  getRoutes(filter?: { organisationId?: number | null; includeInactive?: boolean }): Observable<Route[]> {
    const params: Record<string, string> = {};

    if (filter?.organisationId) {
      params['organisationId'] = String(filter.organisationId);
    }

    if (filter?.includeInactive) {
      params['includeInactive'] = 'true';
    }

    return this.http.get<unknown>(this.routeUrl, { params }).pipe(
      map(response => normalizeApiArray<Route>(response))
    );
  }

  getRouteById(id: number): Observable<Route> {
    return this.http.get<Route>(`${this.routeUrl}/${id}`).pipe(
      map(route => this.withOrderedStops(route))
    );
  }

  createRoute(routeData: RoutePayload): Observable<Route> {
    return this.http.post<Route>(this.routeUrl, routeData);
  }

  updateRoute(id: number, routeData: RoutePayload): Observable<Route> {
    return this.http.put<Route>(`${this.routeUrl}/${id}`, routeData);
  }

  deleteRoute(id: number): Observable<any> {
    return this.http.delete(`${this.routeUrl}/${id}`);
  }

  duplicateRoute(id: number, payload: { routeName?: string; organisationID?: number; statusID?: number } = {}): Observable<Route> {
    return this.http.post<Route>(`${this.routeUrl}/${id}/duplicate`, payload);
  }

  getRouteStops(routeId: number): Observable<RouteStop[]> {
    return this.http.get<unknown>(this.routeStopUrl, { params: { routeId: String(routeId) } }).pipe(
      map(response => this.orderStops(normalizeApiArray<RouteStop>(response)))
    );
  }

  addRouteStop(stopData: RouteStopPayload): Observable<RouteStop> {
    return this.http.post<RouteStop>(this.routeStopUrl, stopData);
  }

  updateRouteStop(stopId: number, stopData: RouteStopPayload): Observable<RouteStop> {
    return this.http.put<RouteStop>(`${this.routeStopUrl}/${stopId}`, stopData);
  }

  deleteRouteStop(stopId: number): Observable<any> {
    return this.http.delete(`${this.routeStopUrl}/${stopId}`);
  }

  getActiveRoutes(): Observable<Route[]> {
    return this.getRoutes();
  }

  // Calculate route distance and duration
  calculateRouteDetails(stops: { latitude: number; longitude: number }[]): Observable<{
    distance: number;
    duration: number;
  }> {
    return this.http.post<{ distance: number; duration: number }>(
      `${this.routeUrl}/calculate`,
      { stops }
    );
  }

  private withOrderedStops(route: Route): Route {
    const stops = route.stops || route.routeStops || [];
    return { ...route, stops: this.orderStops(stops) };
  }

  private orderStops(stops: RouteStop[]): RouteStop[] {
    return [...stops].sort((first, second) => Number(first.stopOrder) - Number(second.stopOrder));
  }
}
